import {
  createFileRoute,
  Outlet,
  redirect,
  useLocation,
} from "@tanstack/react-router";
import { getDefaultStore } from "jotai";
import { Toaster } from "sonner";
import { isAuthenticatedAtom } from "@tubo/store";
import { AppSidebar } from "@client/components/app-sidebar";
import { SettingsMobileNavigation } from "@client/components/settings/settings-mobile-navigation";
import { useIsMobile } from "@tubo/ui";
import { OnboardingController } from "@client/components/onboarding/onboarding-controller";
import { useTheme } from "@client/hooks/use-theme";
import { usePermitSync } from "@client/hooks/use-permit-sync";
import { useTmplSync } from "@client/hooks/use-tmpl-sync";
import { useGetMyFeatures } from "@tubo/api-models/hooks/tenant-feature-api.hooks";

const ROUTE_FEATURES: Array<[string, string]> = [
  ["/search", "ai_search"],
  ["/kb", "knowledge_base"],
  ["/doc-generation", "doc_generation"],
  ["/doc-center", "doc_center"],
  ["/recycle-bin", "recycle_bin"],
  ["/data-center/exports", "export"],
  ["/truth", "truth"],
  ["/data-center/datasources", "data_sources"],
  ["/tenant-settings/audit-logs", "audit_logs"],
  ["/tenant-settings/publications", "publications"],
];

export const Route = createFileRoute("/_app")({
  beforeLoad: ({ location }) => {
    const store = getDefaultStore();
    if (!store.get(isAuthenticatedAtom)) {
      throw redirect({
        to: "/login",
        search: { redirect: location.href },
      });
    }
  },
  component: AppLayout,
});

function AppLayout() {
  const { resolvedTheme } = useTheme();
  // 权限变更 WS 广播 → 失效权限相关查询（目录树/导航/列表可见性），免刷新生效
  usePermitSync();
  // 数据抽取模板变更 WS 广播 → 失效模板列表/详情，跨用户免刷新可见
  useTmplSync();
  const { data: features, isLoading } = useGetMyFeatures({ query: {} });
  const pathname = useLocation().pathname;
  const isMobile = useIsMobile();
  const mobileSettings = isMobile && (pathname.startsWith("/settings/") || pathname.startsWith("/tenant-settings/"));
  // 全屏沉浸形态（主侧栏一并隐藏；域二级目录在各域布局隐藏）：
  // - 数据模型设计器编辑页
  // - Truth 验收轮次评分页 /truth/rounds/{id} / 产物对比详情页 /truth/compares/{id}
  const fullscreenEditor =
    pathname.startsWith("/semantic-platform/data-models/edit") ||
    pathname.startsWith("/truth/rounds/") ||
    pathname.startsWith("/truth/compares/");
  const requiredFeature = ROUTE_FEATURES.find(
    ([prefix]) => pathname === prefix || pathname.startsWith(`${prefix}/`),
  )?.[1];
  if (isLoading)
    return (
      <div className="flex h-screen items-center justify-center text-muted-foreground">
        加载中…
      </div>
    );
  if (requiredFeature && !features?.features?.includes(requiredFeature)) {
    return (
      <div className="flex h-screen items-center justify-center text-muted-foreground">
        该功能尚未对当前租户开放
      </div>
    );
  }

  return (
    <OnboardingController>
      <div className={`flex h-screen overflow-hidden ${mobileSettings ? "flex-col" : ""}`}>
        {!fullscreenEditor && (mobileSettings ? <SettingsMobileNavigation /> : <AppSidebar />)}
        {/* scrollbar-none：外层滚动条隐藏（各页面自管内部滚动），避免切换视图时滚动条闪现引起布局跳动 */}
        <main className="min-h-0 min-w-0 flex-1 overflow-y-auto scrollbar-none">
          <Outlet />
        </main>
        <Toaster
          position="top-center"
          theme={resolvedTheme}
          closeButton
          toastOptions={{
            duration: 5000,
            classNames: {
              toast: "border shadow-lg",
              success: "border-success",
              error: "border-error",
              warning: "border-warning",
              info: "border-info",
            },
          }}
        />
      </div>
    </OnboardingController>
  );
}
